/**
 * KIT Department overview — Hair & Makeup mount (MVP).
 *
 * Renders the curated category grid for the caller's department.
 * Data-driven: categories come from the department JSON module,
 * sorted by `order`. Reused by future departments; no H&M-specific
 * code lives here beyond the `DEPARTMENT` constant supplied by the
 * routing wrapper.
 */
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useRouteBase } from "../../context/ApiBaseContext";
import { getCatalogueForDepartment, getSortedCategories } from "../../data/kitCatalogue";
import { KitCategoryTile } from "../../components/kit/KitCategoryTile";
import { KitAffiliateDisclosure } from "../../components/kit/KitAffiliateDisclosure";
import ProjectHeader from "../../components/ProjectHeader";

export default function KitDepartmentPage({ department }) {
    const { projectId } = useParams();
    const routeBase = useRouteBase();
    const catalogue = useMemo(() => getCatalogueForDepartment(department), [department]);
    const categories = useMemo(() => getSortedCategories(catalogue), [catalogue]);

    return (
        <div data-testid="kit-department-page" className="min-h-screen">
            {/* Iter-158.13.2.20f.29 — KIT landing now renders inside the
                canonical `<ProjectHeader>` — the same application shell
                already mounted by Project Overview, Production
                Management and the Department working surfaces. The
                previous bespoke sticky `<header>` (custom Back button
                `kit-department-back` + local title
                `catalogue.display_name`) has been removed; the shared
                header provides history-aware Back, Home to H&M
                overview, project switcher, Search, Upload, warnings,
                unassigned inbox, and Settings. The subtitle carries
                Production/Department/destination context. Rule:
                "The content changes. The frame stays put."

                Iter-158.13.2.20f.30 — Subtitle trimmed from
                `Hair & Makeup · KIT` to the established Hair & Makeup
                Department context (`Hair & Makeup`). Real-iPhone UAT
                surfaced that keeping "· KIT" in the frame duplicated
                the local page identity ("CURATED / Hair & Makeup KIT")
                shown immediately below. The application shell tells
                the user where they are; page content tells them what
                they are looking at. */}
            <ProjectHeader subtitle="Hair & Makeup" />

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-24">
                {catalogue ? (
                    <>
                        <div className="mb-6">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                Curated
                            </div>
                            <h1
                                data-testid="kit-department-heading"
                                className="font-display text-3xl sm:text-4xl font-black text-white mt-2"
                            >
                                {catalogue.display_name}
                            </h1>
                            {catalogue.tagline ? (
                                <p className="text-neutral-400 text-sm mt-2 leading-relaxed max-w-prose">
                                    {catalogue.tagline}
                                </p>
                            ) : null}
                        </div>

                        {catalogue.fixture ? (
                            <p
                                data-testid="kit-fixture-notice"
                                className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100"
                            >
                                Fixture catalogue — the canonical Hair & Makeup manifest will replace this before release.
                            </p>
                        ) : null}

                        <div
                            data-testid="kit-category-grid"
                            className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                        >
                            {categories.map((c) => (
                                <KitCategoryTile
                                    key={c.slug}
                                    category={c}
                                    to={`${routeBase}/${projectId}/kit/${c.slug}`}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <div
                        data-testid="kit-catalogue-missing"
                        className="rounded-xl border border-dashed border-white/10 p-6 text-center"
                    >
                        <h1 className="font-display text-xl font-bold text-white">
                            KIT is coming soon
                        </h1>
                        <p className="text-neutral-400 text-sm mt-2">
                            A curated catalogue for this department hasn&rsquo;t been published yet.
                        </p>
                    </div>
                )}

                <KitAffiliateDisclosure />
            </main>
        </div>
    );
}
